/**
 * Compila um array de CompiledSlotDTO em uma fila plana de PlayQueueItem,
 * aplicando dayparting, ordenação por prioridade, SOV proporcional e seleção
 * de criativo por peso.
 *
 * SOV (Share of Voice): quando slots de campanha têm shareOfVoice definido,
 * a fila é construída com repetição proporcional usando o GCD das cotas.
 * Ex: SOV=60 e SOV=40 → GCD=20 → slot A aparece 3×, slot B aparece 2× por loop.
 *
 * A fila é recompilada a cada loop completo para re-randomizar criativos
 * e reavaliar dayparting.
 */
import { env } from '@/env'
import type { CompiledSlotDTO, PlayContextSnapshot, PlayQueueItem } from '@/types/player.types'
import { isPlaylistItemActiveNow, isSlotActiveNow } from '@/utils/timezone'
import { weightedRandom } from '@/utils/weighted-random'

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b)
}

function buildCampaignItem(
  slot: CompiledSlotDTO,
  baseContext: Omit<PlayContextSnapshot, 'campaignName' | 'campaignPacingMode' | 'pacingDailyTarget'>
): PlayQueueItem | null {
  if (!slot.creatives || slot.creatives.length === 0) return null
  const creative = weightedRandom(slot.creatives)
  return {
    slotId: slot.slotId,
    slotName: slot.slotName,
    slotType: slot.slotType,
    priority: slot.priority,
    auctionId: slot.auctionId,
    listingId: slot.listingId,
    grantId: slot.grantId,
    assetId: creative.assetId,
    assetUrl: creative.assetUrl,
    hlsUrl: creative.hlsUrl,
    checksum: creative.checksum,
    mimeType: creative.mimeType ?? 'video/mp4',
    durationSeconds: creative.durationSeconds ?? 15,
    campaignId: slot.campaignId,
    guestCampaignId: slot.guestCampaignId,
    creativeId: creative.creativeId,
    qrCode: creative.qrOverlay === false ? undefined : slot.qrCode,
    deviceImpressionCap: slot.deviceImpressionCap,
    minIntervalSeconds: slot.minIntervalSeconds,
    pacingDailyTarget: slot.pacingDailyTarget,
    sovDailyTarget: slot.sovDailyTarget,
    contextSnapshot: {
      ...baseContext,
      campaignName: slot.campaignName,
      campaignPacingMode: slot.campaignPacingMode,
      pacingDailyTarget: slot.pacingDailyTarget
    }
  }
}

/** Mime sintético do item de tela desligada (nunca vem da API). */
export const SCREEN_OFF_MIME_TYPE = 'application/x-castiq-screen-off'
/** Duração de cada item de tela desligada antes de reavaliar a agenda. */
export const SCREEN_OFF_ITEM_SECONDS = 30

export function compile(
  slots: CompiledSlotDTO[],
  timezone: string,
  scheduleVersion: string,
  deviceName: string,
  workspaceName: string,
  isOfflinePlay: boolean,
  scheduleCacheAgeSeconds?: number
): PlayQueueItem[] {
  const queue: PlayQueueItem[] = []

  // 1. Filtrar por dayparting local
  const activeSlots = slots.filter((slot) => isSlotActiveNow(slot, timezone))

  // 2. Ordenar por prioridade (maior primeiro)
  const sorted = [...activeSlots].sort((a, b) => b.priority - a.priority)

  // 3. Separar slots de campanha com SOV definido para calcular repetições proporcionais
  const sovCampaignSlots = sorted.filter(
    (s) => s.slotType === 'campaign' && s.creatives?.length && s.shareOfVoice != null && s.shareOfVoice > 0
  )

  const sovRepetitions = new Map<string, number>()
  if (sovCampaignSlots.length > 0) {
    const sovValues = sovCampaignSlots.map((s) => s.shareOfVoice ?? 0).filter((value) => value > 0)
    if (sovValues.length > 0) {
      const divisor = sovValues.reduce(gcd)
      for (const slot of sovCampaignSlots) {
        const shareOfVoice = slot.shareOfVoice ?? 0
        sovRepetitions.set(slot.slotId, Math.max(1, Math.round(shareOfVoice / divisor)))
      }
    }
  }

  for (const slot of sorted) {
    const baseContext: Omit<PlayContextSnapshot, 'campaignName' | 'campaignPacingMode' | 'pacingDailyTarget'> = {
      deviceName,
      workspaceName,
      slotName: slot.slotName,
      slotType: slot.slotType,
      playerRelease: env.VITE_PLAYER_RELEASE,
      scheduleVersion,
      isPreview: false,
      isOfflinePlay,
      scheduleCacheAgeSeconds
    }

    // F-24 (revisão 2026-10-26): slot de evento screen_off vira um item "tela preta", sem conteúdo nem play log.
    // Reavaliado a cada SCREEN_OFF_ITEM_SECONDS para a tela voltar logo que a janela/trigger acabar.
    if (slot.eventAction === 'screen_off') {
      queue.push({
        slotId: slot.slotId,
        slotName: slot.slotName,
        slotType: slot.slotType,
        priority: slot.priority,
        mimeType: SCREEN_OFF_MIME_TYPE,
        durationSeconds: Math.min(slot.maxDurationSeconds ?? SCREEN_OFF_ITEM_SECONDS, SCREEN_OFF_ITEM_SECONDS),
        triggerRules: slot.triggerRules,
        eventAction: 'screen_off',
        maxDurationSeconds: slot.maxDurationSeconds,
        contextSnapshot: baseContext
      })
      continue
    }

    if (slot.slotType === 'campaign' && slot.creatives && slot.creatives.length > 0) {
      const repetitions = slot.repetitionHint ?? sovRepetitions.get(slot.slotId) ?? 1
      for (let i = 0; i < repetitions; i++) {
        const item = buildCampaignItem(slot, baseContext)
        if (item) queue.push(item)
      }
    } else if (slot.playlistItems && slot.playlistItems.length > 0) {
      // Fixed / Event: um item por entrada na playlist (na ordem sortOrder)
      const items = [...slot.playlistItems]
        .filter((item) => isPlaylistItemActiveNow(item, timezone))
        .sort((a, b) => a.sortOrder - b.sortOrder)
      for (const item of items) {
        if (!item.mimeType && !item.externalUrl && !item.assetUrl) continue

        queue.push({
          slotId: slot.slotId,
          slotName: slot.slotName,
          slotType: slot.slotType,
          priority: slot.priority,
          assetId: item.assetId,
          assetUrl: item.assetUrl,
          hlsUrl: item.hlsUrl,
          checksum: item.checksum,
          externalUrl: item.externalUrl,
          mimeType: item.mimeType ?? 'text/html',
          durationSeconds: item.durationSeconds ?? 10,
          playlistItemId: item.itemId,
          playlistId: slot.playlistId,
          triggerRules: slot.triggerRules,
          eventAction: slot.eventAction,
          maxDurationSeconds: slot.maxDurationSeconds,
          contextSnapshot: baseContext
        })
      }
    }
  }

  return queue
}
